import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Terminal, Shield, Zap, Code2, Layers, CheckCircle2, Radar, Cpu, Sparkles } from 'lucide-react';
import TopBar from '../components/TopBar';
import PageTitle from '../components/PageTitle';

type DemoItem = {
  label: string;
  value: string;
};

// --- Reasoning Workspace Component ---
const MockEditor = () => {
  const { t } = useTranslation('landing');
  const [step, setStep] = useState(0);
  const requestItems = t('home.demo_request_items', { returnObjects: true }) as DemoItem[];
  const traceSteps = t('home.demo_trace_steps', { returnObjects: true }) as string[];
  const deliverables = t('home.demo_deliverables', { returnObjects: true }) as string[];

  useEffect(() => {
    const timer = setInterval(() => {
      setStep((prev) => (prev + 1) % traceSteps.length);
    }, 2000);
    return () => clearInterval(timer);
  }, [traceSteps.length]);

  const activeStep = step % traceSteps.length;

  return (
    <div className="relative w-full max-w-5xl mx-auto mt-16 overflow-hidden rounded-2xl border border-border bg-card text-left shadow-2xl">
      <div className="flex items-center gap-3 border-b border-border bg-muted/40 px-4 py-3">
        <div className="flex shrink-0 space-x-2">
          <div className="h-3 w-3 rounded-full bg-red-500/80" />
          <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
          <div className="h-3 w-3 rounded-full bg-green-500/80" />
        </div>
        <div className="min-w-0 flex-1 text-center text-xs font-mono text-muted-foreground">
          {t('home.demo_window_title')}
        </div>
        <div className="hidden shrink-0 rounded-md border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-primary sm:block">
          {t('home.confidence')}
        </div>
      </div>

      <div className="grid divide-y divide-border bg-background/70 lg:grid-cols-[1.08fr_1fr_0.92fr] lg:divide-x lg:divide-y-0">
        <section className="p-5 md:p-6">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">{t('home.demo_request_title')}</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">{t('home.demo_request_subtitle')}</div>
            </div>
          </div>

          <div className="rounded-lg border border-border/70 bg-card/70 p-4 text-sm leading-6 text-foreground shadow-sm">
            {t('home.demo_prompt')}
          </div>

          <div className="mt-4 space-y-2">
            {requestItems.map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs">
                <span className="shrink-0 font-medium text-muted-foreground">{item.label}</span>
                <span className="text-right font-medium text-foreground">{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="p-5 md:p-6">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">{t('home.demo_trace_title')}</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">{t('home.demo_trace_subtitle')}</div>
            </div>
          </div>

          <div className="space-y-3">
            {traceSteps.map((traceStep, stepIndex) => {
              const isActive = stepIndex === activeStep;
              return (
                <div
                  key={traceStep}
                  className={`flex gap-3 rounded-lg border px-3 py-3 transition-all duration-500 ${isActive
                    ? 'border-primary/40 bg-primary/10 shadow-sm'
                    : 'border-border/60 bg-card/60'
                  }`}
                >
                  <div className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                  }`}
                  >
                    {stepIndex + 1}
                  </div>
                  <div className="text-sm leading-6 text-foreground">{traceStep}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-700"
              style={{ width: `${((activeStep + 1) / traceSteps.length) * 100}%` }}
            />
          </div>
        </section>

        <section className="flex flex-col p-5 md:p-6">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Code2 className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">{t('home.demo_deliverables_title')}</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">{t('home.demo_deliverables_subtitle')}</div>
            </div>
          </div>

          <div className="space-y-3">
            {deliverables.map((deliverable) => (
              <div key={deliverable} className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/60 px-3 py-3 text-sm leading-6 text-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{deliverable}</span>
              </div>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 border-t border-border/60 pt-4 text-xs">
            <div className="rounded-md bg-muted/40 px-3 py-2">
              <div className="font-medium text-muted-foreground">{t('home.demo_ready_label')}</div>
              <div className="mt-1 font-semibold text-foreground">{t('home.demo_ready_value')}</div>
            </div>
            <div className="rounded-md bg-muted/40 px-3 py-2">
              <div className="font-medium text-muted-foreground">{t('home.demo_api_label')}</div>
              <div className="mt-1 font-semibold text-foreground">{t('home.demo_api_value')}</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

// --- Main Page ---
export default function Home() {
  const { user } = useAuth();
  const { t } = useTranslation('landing');
  const heroPoints = t('home.hero_points', { returnObjects: true }) as string[];

  // --- Promotional popup: March 2026 MAX package giveaway ---
  // This promotion runs from March 1 to March 31, 2026.
  // Only shown to non-logged-in users, with a 3-day cooldown stored in localStorage.
  // After March 31st the popup will never appear regardless of other conditions.
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    const PROMO_KEY = 'promo_max_march_dismissed';
    const PROMO_DEADLINE = new Date('2026-04-01T00:00:00').getTime(); // Expires after March 31, 2026
    const COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

    if (user) return;
    if (Date.now() >= PROMO_DEADLINE) return;

    const lastDismissed = localStorage.getItem(PROMO_KEY);
    if (lastDismissed) {
      const elapsed = Date.now() - Number(lastDismissed);
      if (elapsed < COOLDOWN_MS) return;
    }
    setShowPromo(true);
  }, [user]);

  const dismissPromo = () => {
    localStorage.setItem('promo_max_march_dismissed', String(Date.now()));
    setShowPromo(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30">
      <PageTitle 
        titleKey="home.title" 
        namespace="landing" 
        descriptionKey="home.hero_desc"
      />
      <TopBar />

      {/* ===== Hero Section ===== */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

        <div className="relative mx-auto max-w-6xl px-4 text-center z-10">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary shadow-sm animate-slide-up" style={{ animationDelay: '0.05s', animationFillMode: 'both' }}>
            <Sparkles className="h-4 w-4" />
            {t('home.hero_badge')}
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-6 animate-slide-up" style={{ animationDelay: '0.1s', animationFillMode: 'both' }}>
            {t('home.hero_title_1')}<br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-foreground to-muted-foreground">{t('home.hero_title_2')}</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-6 animate-slide-up" style={{ animationDelay: '0.2s', animationFillMode: 'both' }}>
            {t('home.hero_desc')}
          </p>

          <div className="mb-8 flex flex-wrap items-center justify-center gap-3 animate-slide-up" style={{ animationDelay: '0.25s', animationFillMode: 'both' }}>
            {heroPoints.map((point) => (
              <div key={point} className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1.5 text-sm font-medium text-foreground shadow-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                {point}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-slide-up" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
            <Link
              to={user ? '/console' : '/auth'}
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md bg-primary text-primary-foreground font-medium transition-all hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]"
            >
              {user ? t('home.start_analyzing') : t('home.start_free')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/arena"
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md border border-input bg-background font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Sparkles className="w-4 h-4" />
              {t('home.try_arena')}
            </Link>
          </div>

          <div className="animate-slide-up" style={{ animationDelay: '0.4s', animationFillMode: 'both' }}>
            <MockEditor />
          </div>
        </div>
      </section>

      {/* ===== Stats Section ===== */}
      <section className="py-12 border-y border-border bg-muted/20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-border/50">
            {[
              { value: 'GPT 5.5', label: t('home.stats.samples') },
              { value: 'OPUS 4.8', label: t('home.stats.accuracy') },
              { value: 'Gemini 3.1 Pro', label: t('home.stats.latency') },
              { value: '3 Models', label: t('home.stats.params') },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center justify-center">
                <div className="text-3xl md:text-4xl font-bold tracking-tight">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Bento Grid Features ===== */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tighter mb-4">{t('home.beyond_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              {t('home.beyond_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[240px]">
            <div className="md:col-span-2 md:row-span-2 rounded-2xl border border-border bg-card p-8 flex flex-col justify-between group hover:border-primary/50 transition-colors overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -mr-20 -mt-20 transition-opacity group-hover:opacity-100 opacity-50"></div>
              <div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <Code2 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{t('home.semantic_title')}</h3>
                <p className="text-muted-foreground max-w-md leading-relaxed">
                  {t('home.semantic_desc')}
                </p>
              </div>
              <div className="mt-8 font-mono text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg border border-border/50">
                <span className="text-primary">const</span> <span className="text-blue-400">workflow</span> = {'{'} <br />
                &nbsp;&nbsp;models: <span className="text-green-700 dark:text-green-400">["gpt-5.5", "opus-4.8", "gemini-3.1-pro"]</span>,<br />
                &nbsp;&nbsp;output: <span className="text-green-700 dark:text-green-400">"compare + recommend"</span><br />
                {'}'};
              </div>
            </div>

            {/* Small Feature 1 */}
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col group hover:border-primary/50 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Layers className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2">{t('home.multi_arch')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('home.multi_arch_desc')}
              </p>
            </div>

            {/* Small Feature 2 */}
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col group hover:border-primary/50 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-lg font-bold mb-2">{t('home.realtime')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('home.realtime_desc')}
              </p>
            </div>

            {/* Wide Feature */}
            <div className="md:col-span-3 rounded-2xl border border-border bg-card p-8 flex flex-col md:flex-row items-center gap-8 group hover:border-primary/50 transition-colors">
              <div className="flex-1">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{t('home.vuln_title')}</h3>
                <p className="text-muted-foreground">
                  {t('home.vuln_desc')}
                </p>
              </div>
              <div className="flex-1 w-full bg-muted/30 rounded-xl border border-border/50 p-6 flex items-center justify-center">
                <div className="flex items-center gap-3 text-sm font-mono">
                  <span className="text-primary">{t('home.alert_label')}</span>
                  <span className="text-muted-foreground">{t('home.vuln_detected')}</span>
                  <span className="text-foreground bg-muted px-2 py-1 rounded">{t('home.demo_model_label')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Products Section ===== */}
      <section className="py-16 md:py-24 border-t border-border bg-muted/10">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-12 text-center">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tighter mb-4">{t('home.enterprise_title')}</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('home.enterprise_desc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* AI Console */}
            <div className="rounded-2xl border border-border bg-card p-8 md:p-10 flex flex-col">
              <div className="mb-6">
                <Shield className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-2">{t('home.malware_analysis_title')}</h3>
                <p className="text-muted-foreground">{t('home.malware_analysis_subtitle')}</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {(t('home.malware_analysis_features', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/console" className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors">
                {t('home.explore_malware_analysis')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* API */}
            <div className="rounded-2xl border border-border bg-card p-8 md:p-10 flex flex-col">
              <div className="mb-6">
                <Sparkles className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-2">{t('home.finetuning_title')}</h3>
                <p className="text-muted-foreground">{t('home.finetuning_subtitle')}</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {(t('home.finetuning_features', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/docs/overview/quickstart" className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors">
                {t('home.explore_finetuning')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Automation */}
            <div className="rounded-2xl border border-border bg-card p-8 md:p-10 flex flex-col">
              <div className="mb-6">
                <Radar className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-2">{t('home.vuln_mining_title')}</h3>
                <p className="text-muted-foreground">{t('home.vuln_mining_subtitle')}</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {(t('home.vuln_mining_features', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/plans" className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors">
                {t('home.explore_vuln_mining')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Model Access */}
            <div className="rounded-2xl border border-border bg-card p-8 md:p-10 flex flex-col">
              <div className="mb-6">
                <Cpu className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-2">{t('home.reverse_model_title')}</h3>
                <p className="text-muted-foreground">{t('home.reverse_model_subtitle')}</p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {(t('home.reverse_model_features', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/arena" className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors">
                {t('home.explore_reverse_model')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA Section ===== */}
      <section className="py-20 md:py-24 border-t border-border">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter mb-6">
            {t('home.cta_title')}
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            {t('home.cta_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={user ? '/console' : '/auth'}
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md bg-primary text-primary-foreground font-medium transition-all hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98]"
            >
              {user ? t('home.open_console') : t('home.start_free')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/arena"
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md border border-input bg-background font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {t('home.try_arena')}
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="border-t border-border py-12 bg-muted/20">
        <div className="mx-auto max-w-6xl px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-bold tracking-tight">
            <Terminal className="w-5 h-5" />
            {import.meta.env.VITE_PRODUCT_NAME || 'Etherworks'}
          </div>
          <div className="text-sm text-muted-foreground text-center">
            <div>{t('home.copyright', { year: new Date().getFullYear() })}</div>
            {import.meta.env.VITE_ICP_RECORD && (
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors"
              >
                {import.meta.env.VITE_ICP_RECORD}
              </a>
            )}
          </div>
        </div>
      </footer>

      {/* --- March 2025 promotional popup --- */}
      {showPromo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-2xl text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">{t('promo_title')}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">{t('promo_message')}</p>
            <button
              onClick={dismissPromo}
              className="inline-flex items-center justify-center h-10 px-8 rounded-md bg-primary text-primary-foreground font-medium transition-colors hover:bg-primary/90"
            >
              {t('promo_ok')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
